import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Event, CreateEventRequest } from '../types';
import { apiPost, ApiError } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const CreateEvent: React.FC = () => {
  const [formData, setFormData] = useState<CreateEventRequest>({
    name: '',
    description: '',
    max_team_size: 5,
    registration_deadline: '',
    submission_deadline_overview: '',
    submission_deadline_media: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const { token, user } = useAuth();
  const navigate = useNavigate();

  // Check if current user has organizer role (case-insensitive)
  const isOrganizer = user?.roles?.some(role => role.toUpperCase() === 'ORGANIZER') || false;

  // Redirect non-organizers
  React.useEffect(() => {
    if (user && !isOrganizer) {
      navigate('/events');
    }
  }, [user, isOrganizer, navigate]);

  // Don't render the form for non-organizers
  if (user && !isOrganizer) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '40px' }}>
        <h2>Access Denied</h2>
        <p>Only organizers can create events.</p>
      </div>
    );
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'max_team_size' ? Number(value) : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token) {
      setError('You must be logged in to create an event');
      return;
    }

    // Basic validation
    if (!formData.name.trim()) {
      setError('Event name is required');
      return;
    }
    
    if (!formData.description.trim()) {
      setError('Event description is required');
      return;
    }

    if (formData.max_team_size < 1) {
      setError('Maximum team size must be at least 1');
      return;
    }

    // Validate dates
    const regDeadline = new Date(formData.registration_deadline);
    const overviewDeadline = new Date(formData.submission_deadline_overview);
    const mediaDeadline = new Date(formData.submission_deadline_media);
    
    if (regDeadline >= overviewDeadline) {
      setError('Registration deadline must be before overview submission deadline');
      return;
    }
    
    if (overviewDeadline >= mediaDeadline) {
      setError('Overview submission deadline must be before media submission deadline');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const event = await apiPost<Event>('/events', formData, token);
      console.log('Event created:', event);
      navigate('/events');
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Failed to create event');
      }
    } finally {
      setLoading(false);
    }
  };

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().slice(0, 16);

  const nextWeek = new Date();
  nextWeek.setDate(nextWeek.getDate() + 7);
  const nextWeekStr = nextWeek.toISOString().slice(0, 16);

  const twoWeeks = new Date();
  twoWeeks.setDate(twoWeeks.getDate() + 14);
  const twoWeeksStr = twoWeeks.toISOString().slice(0, 16);

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <button onClick={() => navigate('/events')} className="btn btn-secondary">
          ← Back to Events
        </button>
      </div>

      <div className="card">
        <h1>Create New Event</h1>
        
        {error && <div className="error" style={{ marginBottom: '20px' }}>{error}</div>}
        
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Event Name*
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Enter event name"
              required
              disabled={loading}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Description*
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Describe your hackathon event"
              rows={4}
              required
              disabled={loading}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Maximum Team Size*
            </label>
            <input
              type="number"
              name="max_team_size"
              value={formData.max_team_size}
              onChange={handleInputChange}
              className="form-input"
              min={1}
              required
              disabled={loading}
            />
            <small style={{ color: '#666' }}>Default number of members allowed per team</small>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Registration Deadline*
            </label>
            <input
              type="datetime-local"
              name="registration_deadline"
              value={formData.registration_deadline}
              onChange={handleInputChange}
              className="form-input"
              min={tomorrowStr}
              required
              disabled={loading}
            />
            <small style={{ color: '#666' }}>When team registration closes</small>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Overview Submission Deadline*
            </label>
            <input
              type="datetime-local"
              name="submission_deadline_overview"
              value={formData.submission_deadline_overview}
              onChange={handleInputChange}
              className="form-input"
              min={nextWeekStr}
              required
              disabled={loading}
            />
            <small style={{ color: '#666' }}>When project overviews are due</small>
          </div>

          <div style={{ marginBottom: '30px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Media Submission Deadline*
            </label>
            <input
              type="datetime-local"
              name="submission_deadline_media"
              value={formData.submission_deadline_media}
              onChange={handleInputChange}
              className="form-input"
              min={twoWeeksStr}
              required
              disabled={loading}
            />
            <small style={{ color: '#666' }}>When final presentations/demos are due</small>
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="submit"
              className="btn"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Event'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/events')}
              className="btn btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateEvent;
